import { useState } from 'react';
import { toast } from 'sonner';
import { t } from '../i18n';

/**
 * Custom hook for file upload handling with drag & drop support.
 */
export function useFileUpload(maxFiles = 3, maxSizeMB = 10) {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    if (files.length + newFiles.length > maxFiles) {
      toast.error(t('toastMaxFiles', { n: maxFiles }));
      return;
    }
    setFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target.classList.contains('file-upload-zone')) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    if (files.length + droppedFiles.length > maxFiles) {
      toast.error(t('toastMaxFiles', { n: maxFiles }));
      return;
    }

    const validTypes = ['.pdf', '.jpg', '.jpeg', '.png'];
    const invalidFiles = droppedFiles.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return !validTypes.includes(ext);
    });

    if (invalidFiles.length > 0) {
      toast.error(t('toastFileTypeError'));
      return;
    }

    const maxSize = maxSizeMB * 1024 * 1024;
    const oversizedFiles = droppedFiles.filter(file => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      toast.error(t('toastFileSizeError', { n: maxSizeMB }));
      return;
    }

    setFiles(prev => [...prev, ...droppedFiles]);
    toast.success(t('toastFilesAdded', { n: droppedFiles.length }));
  };

  return {
    files,
    isDragging,
    handleFileChange,
    removeFile,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
  };
}
